import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Dispatch, IRootState, useRematchDispatch } from 'rematch/store';
import { EVENTCODE } from 'configs/active/constants';

// Minimum time between two manual reloads.
const RELOAD_COOLDOWN_MS = 60 * 1000;
// Persisted so the cooldown survives a page refresh. Keyed by event so a new year starts clean.
const STORAGE_KEY = `${EVENTCODE}:lastReload`;

const _readLastReload = (): number => {
	try {
		const value = Number(window.localStorage.getItem(STORAGE_KEY));
		return Number.isFinite(value) ? value : 0;
	} catch {
		return 0;
	}
};

const _writeLastReload = (ts: number) => {
	try {
		window.localStorage.setItem(STORAGE_KEY, String(ts));
	} catch {
		// localStorage unavailable (private mode, blocked) - cooldown then only lives in memory
	}
};

export const ReloadButton = () => {
	const [lastReload, setLastReload] = useState<number>(_readLastReload);
	const [now, setNow] = useState<number>(Date.now());

	const decryptedToken = useSelector((store: IRootState) => store.auth.decryptedToken);
	const reloadAll = useRematchDispatch((dispatch: Dispatch) => dispatch.data.reloadAll);

	const remainingMs = Math.max(0, lastReload + RELOAD_COOLDOWN_MS - now);
	const isCoolingDown = remainingMs > 0;

	// Tick once a second while the cooldown runs so the countdown and the disabled state stay current.
	useEffect(() => {
		if (!isCoolingDown) {
			return;
		}
		const interval = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(interval);
	}, [isCoolingDown]);

	if (!decryptedToken) {
		return null;
	}

	const _onClick = () => {
		// Only logged in users may reload; the button is not rendered otherwise, this is a second guard.
		if (!decryptedToken || isCoolingDown) {
			return;
		}
		const ts = Date.now();
		setLastReload(ts);
		_writeLastReload(ts);
		setNow(ts);
		reloadAll({});
	};

	const remainingSeconds = Math.ceil(remainingMs / 1000);
	const title = isCoolingDown ? `Reload in ${remainingSeconds}s` : 'Reload';

	return (
		<button
			type="button"
			onClick={_onClick}
			disabled={isCoolingDown}
			className={`ml-auto border px-2 py-1 text-xs lg:text-sm font-bold ${
				isCoolingDown ? 'border-lightgray text-lightgray cursor-default' : 'border-white text-white cursor-pointer'
			}`}
		>
			{title}
		</button>
	);
};
